import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Clock, CheckCircle, ArrowRight, Phone, Zap } from "lucide-react";

const SITE = SITE_URL;

const faqs = [
  {
    q: "Can I book a stroller rental for the same day near Disney World?",
    a: "Same-day stroller rental is available subject to inventory. We recommend booking as early in the day as possible — contact us by phone or text at 689-345-7368 and we will confirm availability and arrange delivery within your window. Same-day delivery is not guaranteed but we do our best to accommodate last-minute requests.",
  },
  {
    q: "What is the last-minute booking cutoff for Disney stroller rentals?",
    a: "We ask for at least 24 hours' notice for standard bookings. If you need a stroller with less than 24 hours' notice, call or text 689-345-7368 directly — we accommodate last-minute requests based on availability and our current schedule.",
  },
  {
    q: "What if my stroller rental is urgent — can I call and book immediately?",
    a: "Yes. For urgent or same-day requests, call or text us at 689-345-7368. We will check availability and, if we can accommodate, arrange delivery to your Disney resort or Orlando hotel as soon as our schedule allows.",
  },
  {
    q: "Do you deliver last-minute strollers to all Disney World resorts?",
    a: "Yes. When same-day or last-minute delivery is available, we deliver to every Walt Disney World resort, Universal Orlando, vacation homes and area hotels. All Disney resort deliveries require an in-person lobby meeting — we cannot leave equipment with bell services.",
  },
  {
    q: "Is last-minute stroller rental more expensive?",
    a: "Our standard rental pricing applies regardless of how far in advance you book. There is no last-minute surcharge. Delivery is always free — no early-delivery surcharges.",
  },
  {
    q: "What strollers are available for last-minute rental?",
    a: "Availability varies by date. We carry single and double strollers from Baby Jogger, BOB, Nuna, UPPAbaby and Zoe. Call or text us and we will tell you exactly which models are available for your dates.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Last-Minute Stroller Rental Disney World", item: `${SITE}/last-minute-stroller-rental-disney-world` },
  ],
};

const steps = [
  { step: "1", title: "Call or text us", body: "Reach us at 689-345-7368. Tell us your resort, the dates you need, and which stroller type you are looking for." },
  { step: "2", title: "Confirm availability", body: "We check our live inventory and schedule. If we can accommodate your request, we confirm the model, delivery window and price on the spot." },
  { step: "3", title: "We deliver to you", body: "We meet you in the lobby of your Disney resort within your agreed window. Your stroller arrives sanitized and ready to go." },
];

export default function LastMinuteStrollerRental() {
  return (
    <>
      <PageSEO
        title="Same-Day & Last-Minute Stroller Rental Near Disney World | Enchanted Gear Rentals"
        description="Need a stroller at Disney World today? Enchanted Gear Rentals offers same-day and last-minute stroller delivery to Disney resorts and Orlando hotels. Call or text 689-345-7368 to check availability."
        canonical={`${SITE}/last-minute-stroller-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Last-Minute Stroller Rental</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-[#FFD166]/10 border border-[#FFD166]/30 text-[#FFD166] text-sm font-medium px-4 py-1.5 rounded-full mb-5">
              <Zap className="w-4 h-4" /> Same-day delivery subject to availability
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Same-Day &amp; Last-Minute Stroller Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Already at your Disney resort and need a stroller? Arriving tomorrow and forgot to book? Enchanted Gear Rentals handles last-minute and same-day stroller requests for Disney World, Universal Orlando, and greater Orlando — call or text us and we will do everything we can to get a stroller to you.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <a href="tel:+16893457368">
                <button className="btn-primary" data-testid="lastminute-cta-top">
                  <Phone className="inline w-4 h-4 mr-2" />Call or Text 689-345-7368
                </button>
              </a>
              <Link to="/#trip-setup">
                <button className="btn-ghost">Check Online Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button>
              </Link>
            </div>
            <p className="mt-4 text-xs text-[#B9C8D5]">For same-day requests, calling or texting is the fastest way to confirm availability.</p>
          </div>
        </section>

        {/* How it works */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">How last-minute stroller rental works</h2>
          <p className="text-[#B9C8D5] mb-10 max-w-2xl">Same-day and last-minute requests follow a slightly different path than standard online bookings. Here is exactly what happens when you reach out.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.step} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
                <div className="w-9 h-9 rounded-full bg-[#FFD166]/15 text-[#FFD166] font-serif text-lg flex items-center justify-center mb-4">{s.step}</div>
                <h3 className="font-semibold text-[#F6F1E4] mb-2">{s.title}</h3>
                <p className="text-sm text-[#B9C8D5]">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* What's available */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Strollers available for last-minute rental</h2>
          <p className="text-[#B9C8D5] mb-8 max-w-2xl">We carry a curated range of premium strollers. Availability on short notice depends on current inventory. These are the models most commonly available:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { brand: "Baby Jogger", model: "City Mini GT2 Single", link: "/strollers/baby-jogger-city-mini-gt2-single", note: "Lightweight, one-hand fold. Great for parks." },
              { brand: "Baby Jogger", model: "City Mini GT2 Double", link: "/strollers/baby-jogger-city-mini-gt2-double", note: "Best-selling double for two park-ready kids." },
              { brand: "Zoe", model: "V2 Twin Double", link: "/strollers/zoe-v2-twin", note: "Ultra-light double. Folds flat for Disney transport." },
              { brand: "BOB", model: "Single Stroller", link: "/strollers/bob-single-stroller", note: "All-terrain single. Perfect for resort paths." },
              { brand: "Nuna", model: "MIXX", link: "/strollers/nuna-mixx", note: "Smooth suspension. Tall handlebar for parents." },
              { brand: "UPPAbaby", model: "VISTA V2", link: "/strollers/uppababy-vista-v2", note: "Full-size single with huge basket." },
            ].map((s) => (
              <Link key={s.model} to={s.link} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all">
                <div className="text-xs text-[#FFD166] font-medium mb-1">{s.brand}</div>
                <div className="font-semibold text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{s.model}</div>
                <div className="text-sm text-[#B9C8D5] mt-1">{s.note}</div>
              </Link>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#B9C8D5]">Actual availability varies. <a href="tel:+16893457368" className="text-[#FFD166] hover:underline">Call or text us</a> to confirm which models are available for your dates.</p>
        </section>

        {/* Important notes */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">What to know about same-day delivery</h2>
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl">
            {[
              { icon: CheckCircle, title: "Available when inventory permits", body: "Same-day and last-minute delivery depends on what is available. We fill standard advance bookings first, so same-day is on a best-effort basis. Calling early in the day gives you the best chance." },
              { icon: Clock, title: "Flexible 1-hour delivery windows", body: "We offer 1-hour delivery windows from 7 am to 10 pm. We will confirm the earliest available window when you call." },
              { icon: CheckCircle, title: "No last-minute surcharge", body: "Our normal rental pricing applies for last-minute bookings. Delivery is always free." },
              { icon: CheckCircle, title: "In-person delivery at Disney resorts", body: "Disney World requires us to meet you in the lobby — we cannot leave equipment with bell services. For last-minute bookings, we coordinate the handoff time directly with you." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F6F1E4]">{title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Frequently asked questions</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related pages */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Also useful</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Disney World stroller rentals", to: "/disney-world-stroller-rentals" },
              { label: "Double stroller rentals", to: "/double-stroller-rentals-orlando" },
              { label: "Orlando stroller rentals", to: "/stroller-rentals-orlando" },
              { label: "Crib rental near Disney", to: "/crib-rental-disney-world" },
              { label: "Baby gear rental near Disney", to: "/baby-gear-rental-disney-world" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Need a stroller today?</h2>
            <p className="text-[#B9C8D5] mb-6">Call or text us now to check same-day and last-minute availability. Or enter your dates online to check instantly.</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:+16893457368"><button className="btn-primary" data-testid="lastminute-cta-bottom"><Phone className="inline w-4 h-4 mr-2" />689-345-7368</button></a>
              <Link to="/#trip-setup"><button className="btn-ghost">Check Online Availability</button></Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
